import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { Dialog, DialogContent } from '../../ui/Dialog.js';
import { Button } from '../../ui/Button.js';
import { Banner } from '../../ui/Banner.js';
import { Field } from '../../ui/Field.js';

/**
 * Adding an administrator.
 *
 * ── WHY THE ROLE HAS NO DEFAULT WORTH TRUSTING ────────────────────────────
 *
 * It opens on `support`, the least of the three, because every other default
 * is worse: `admin` hands out the ability to destroy a customer's books to
 * whoever did not read the field, and `super_admin` hands out the roster
 * itself. Starting at the bottom means a mistake here under-grants, and
 * under-granting is a message from a colleague rather than an incident.
 *
 * The hint changes with the selection, so the consequence is on screen at the
 * moment of choosing rather than in documentation nobody opens.
 *
 * ── THE ERROR STAYS IN THE DIALOG ─────────────────────────────────────────
 *
 * A refusal here is usually about what was typed — an address already on the
 * roster, an address that is not an address. Closing the dialog would discard
 * it and make them start again from an empty form to fix one character.
 */
const ROLES = ['support', 'admin', 'super_admin'] as const;

export function AddAdminDialog({
  onCancel,
  onAdd,
}: {
  onCancel: () => void;
  onAdd: (input: { email: string; name?: string; role: string }) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('support');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const describe = (r: string): string => {
    if (r === 'support') return t('admins.roleSupport');
    if (r === 'admin') return t('admins.roleAdmin');
    return t('admins.roleSuper');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        title={t('admins.addTitle')}
        description={t('admins.addBody')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={working}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={email.trim() === '' || working}
              onClick={() => {
                setError(null);
                setWorking(true);
                void onAdd({ email: email.trim(), name: name.trim() || undefined, role })
                  .catch((err: unknown) => {
                    setError(err instanceof Error ? err.message : t('admins.changeFailed'));
                  })
                  .finally(() => setWorking(false));
              }}
            >
              {working ? t('admins.adding') : t('admins.add')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {error ? (
            <Banner intent="danger" title={t('admins.changeRefusedTitle')}>
              {error}
            </Banner>
          ) : null}

          <Field label={t('admins.email')} hint={t('admins.emailHint')} required>
            {(field) => (
              <input
                {...field}
                type="email"
                value={email}
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>

          <Field label={t('admins.name')} hint={t('admins.nameHint')}>
            {(field) => (
              <input
                {...field}
                type="text"
                value={name}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>

          <Field label={t('admins.roleLabel')} hint={describe(role)} required>
            {(field) => (
              <select
                {...field}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}
